/**
 * Carrier Change Timeline Component
 *
 * Displays a visual timeline of carrier information changes
 * including name, address, officers, and other significant changes
 */

import React, { useState } from 'react';
import { X, AlertTriangle, Info, TrendingUp, MapPin, User, Phone, Building } from 'lucide-react';

interface CarrierChange {
  id: number;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  change_type: string;
  change_detected_at: string;
  detected_by: string;
  data_source: string;
  is_significant: boolean;
  change_category: string;
  suspicious_change: boolean;
  suspicion_reason: string | null;
}

interface ChangesByCategory {
  identity: CarrierChange[];
  location: CarrierChange[];
  contact: CarrierChange[];
  officers: CarrierChange[];
  status: CarrierChange[];
  fleet_size: CarrierChange[];
  bulk_change: CarrierChange[];
  other: CarrierChange[];
}

interface RiskAssessment {
  hasFrequentChanges: boolean;
  hasLocationChanges: boolean;
  hasOfficerChanges: boolean;
  hasBulkChanges: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface MCS150Snapshot {
  id?: string;
  snapshot_date: string;
  power_units?: number;
  drivers?: number;
  mcs_150_mileage?: string;
  [key: string]: unknown;
}

interface HistoricStats {
  dotNumber: string;
  yearsOfData?: number;
  historicalSnapshots: MCS150Snapshot[];
  summary?: {
    totalChanges: number;
    significantChanges: number;
  };
}

interface CarrierChangeTimelineProps {
  dotNumber: number;
  changes: CarrierChange[];
  changesByCategory: ChangesByCategory;
  riskAssessment: RiskAssessment;
  summary: {
    totalChanges: number;
    suspiciousChanges: number;
    significantChanges: number;
    firstChangeDate: string | null;
    lastChangeDate: string | null;
  };
  historicStats?: HistoricStats;
  onClose: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  legal_name: 'Legal Name',
  dba_name: 'DBA Name',
  phy_street: 'Physical Street',
  phy_city: 'Physical City',
  phy_state: 'Physical State',
  phone: 'Phone Number',
  email_address: 'Email Address',
  company_officer_1: 'Primary Officer',
  company_officer_2: 'Secondary Officer',
  status_code: 'Status',
  power_units: 'Power Units',
  total_drivers: 'Total Drivers',
  _summary: 'Bulk Changes'
};

const CATEGORY_ICONS: Record<string, any> = {
  identity: Building,
  location: MapPin,
  contact: Phone,
  officers: User,
  status: AlertTriangle,
  fleet_size: TrendingUp,
  bulk_change: AlertTriangle,
  other: Info
};

const CATEGORY_COLORS: Record<string, string> = {
  identity: '#ef4444',
  location: '#f59e0b',
  contact: '#3b82f6',
  officers: '#8b5cf6',
  status: '#ef4444',
  fleet_size: '#10b981',
  bulk_change: '#dc2626',
  other: '#6b7280'
};

export default function CarrierChangeTimeline({
  dotNumber,
  changes,
  changesByCategory,
  riskAssessment,
  summary,
  historicStats,
  onClose
}: CarrierChangeTimelineProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedChanges, setExpandedChanges] = useState<Set<number>>(new Set());

  const toggleChangeExpand = (changeId: number) => {
    const newExpanded = new Set(expandedChanges);
    if (newExpanded.has(changeId)) {
      newExpanded.delete(changeId);
    } else {
      newExpanded.add(changeId);
    }
    setExpandedChanges(newExpanded);
  };

  const filteredChanges = selectedCategory
    ? changesByCategory[selectedCategory as keyof ChangesByCategory] || []
    : changes;

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'HIGH': return '#dc2626';
      case 'MEDIUM': return '#f59e0b';
      case 'LOW': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '1200px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
              Carrier Change History - DOT {dotNumber}
            </h2>
            <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
              {summary.totalChanges} changes tracked
              {summary.suspiciousChanges > 0 && ` • ${summary.suspiciousChanges} suspicious`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Risk Assessment Banner */}
        {riskAssessment.riskLevel !== 'LOW' && (
          <div style={{
            padding: '16px 24px',
            background: riskAssessment.riskLevel === 'HIGH' ? '#fee2e2' : '#fef3c7',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertTriangle size={20} color={getRiskColor(riskAssessment.riskLevel)} />
            <div>
              <div style={{ fontWeight: 600, color: getRiskColor(riskAssessment.riskLevel) }}>
                {riskAssessment.riskLevel} RISK - Change Pattern Detected
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>
                {riskAssessment.hasFrequentChanges && 'Multiple identity changes • '}
                {riskAssessment.hasLocationChanges && 'Multiple location changes • '}
                {riskAssessment.hasOfficerChanges && 'Officer changes detected • '}
                {riskAssessment.hasBulkChanges && 'Bulk changes detected'}
              </div>
            </div>
          </div>
        )}

        {/* Category Filters */}
        <div style={{
          padding: '16px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          overflowX: 'auto'
        }}>
          <button
            onClick={() => setSelectedCategory(null)}
            style={{
              padding: '6px 12px',
              borderRadius: '20px',
              border: selectedCategory === null ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: selectedCategory === null ? '#eff6ff' : 'white',
              color: selectedCategory === null ? '#3b82f6' : '#6b7280',
              fontSize: '0.875rem',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            All ({changes.length})
          </button>
          {Object.entries(changesByCategory).map(([category, categoryChanges]) => {
            if (categoryChanges.length === 0) return null;
            const Icon = CATEGORY_ICONS[category];
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: selectedCategory === category ? `2px solid ${CATEGORY_COLORS[category]}` : '1px solid #d1d5db',
                  background: selectedCategory === category ? `${CATEGORY_COLORS[category]}15` : 'white',
                  color: selectedCategory === category ? CATEGORY_COLORS[category] : '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Icon size={14} />
                {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} ({categoryChanges.length})
              </button>
            );
          })}
        </div>

        {/* Changes Timeline */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* MCS-150 Census Changes Section */}
          {historicStats && historicStats.historicalSnapshots && historicStats.historicalSnapshots.length > 1 && (() => {
            // Filter to show only snapshots where values changed
            const mcsChanges = historicStats.historicalSnapshots.filter((snapshot, index: number) => {
              if (index === 0) return true; // Always show current
              if (index === historicStats.historicalSnapshots.length - 1) return true; // Always show oldest

              const nextSnapshot = historicStats.historicalSnapshots[index + 1];
              const powerChanged = (snapshot.power_units || 0) !== (nextSnapshot?.power_units || 0);
              const driversChanged = (snapshot.drivers || 0) !== (nextSnapshot?.drivers || 0);
              const mileageChanged = (snapshot.mcs_150_mileage || 0) !== (nextSnapshot?.mcs_150_mileage || 0);

              return powerChanged || driversChanged || mileageChanged;
            });

            return (
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px', color: '#111827' }}>
                  MCS-150 Census Changes ({mcsChanges.length} significant updates)
                </h3>
                <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', color: '#6b7280', fontWeight: 600 }}>Date</th>
                        <th style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>Power Units</th>
                        <th style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>Drivers</th>
                        <th style={{ padding: '12px', textAlign: 'center', color: '#6b7280', fontWeight: 600 }}>Mileage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mcsChanges.map((snapshot, idx: number) => {
                        const origIndex = historicStats.historicalSnapshots.indexOf(snapshot);
                        const prevSnapshot = origIndex < historicStats.historicalSnapshots.length - 1
                          ? historicStats.historicalSnapshots[origIndex + 1]
                          : null;

                        const powerChange = prevSnapshot ? (snapshot.power_units || 0) - (prevSnapshot.power_units || 0) : 0;
                        const driverChange = prevSnapshot ? (snapshot.drivers || 0) - (prevSnapshot.drivers || 0) : 0;

                        return (
                          <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6', background: idx === 0 ? '#eff6ff' : 'white' }}>
                            <td style={{ padding: '12px', fontWeight: idx === 0 ? 600 : 400 }}>
                              {new Date(snapshot.snapshot_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                              {idx === 0 && <span style={{ marginLeft: '8px', fontSize: '0.7rem', color: '#3b82f6', fontWeight: 600 }}>(CURRENT)</span>}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: idx === 0 ? 600 : 400 }}>
                              {snapshot.power_units || 0}
                              {powerChange !== 0 && (
                                <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: powerChange > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                                  {powerChange > 0 ? '+' : ''}{powerChange}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', fontWeight: idx === 0 ? 600 : 400 }}>
                              {snapshot.drivers || 0}
                              {driverChange !== 0 && (
                                <span style={{ marginLeft: '6px', fontSize: '0.75rem', color: driverChange > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                                  {driverChange > 0 ? '+' : ''}{driverChange}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: '#6b7280' }}>
                              {snapshot.mcs_150_mileage ? `${(parseInt(snapshot.mcs_150_mileage) / 1000000).toFixed(1)}M` : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Carrier Information Changes Section */}
          {filteredChanges.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '16px', color: '#111827' }}>
                Carrier Information Changes ({filteredChanges.length} changes)
              </h3>
            </div>
          )}

          {filteredChanges.length === 0 && (!historicStats || !historicStats.historicalSnapshots || historicStats.historicalSnapshots.length <= 1) ? (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              color: '#9ca3af'
            }}>
              <Info size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <div>No changes recorded yet</div>
              <div style={{ fontSize: '0.875rem', marginTop: '8px' }}>
                Changes will be tracked automatically when carrier information is updated
              </div>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '24px',
                bottom: '24px',
                width: '2px',
                background: '#e5e7eb'
              }} />

              {/* Changes */}
              {filteredChanges.map((change, index) => {
                const isExpanded = expandedChanges.has(change.id);
                const Icon = CATEGORY_ICONS[change.change_category] || Info;
                const color = CATEGORY_COLORS[change.change_category] || '#6b7280';

                return (
                  <div
                    key={change.id}
                    style={{
                      position: 'relative',
                      paddingLeft: '56px',
                      marginBottom: index < filteredChanges.length - 1 ? '24px' : 0
                    }}
                  >
                    {/* Timeline dot */}
                    <div style={{
                      position: 'absolute',
                      left: '12px',
                      top: '4px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      background: change.suspicious_change ? '#ef4444' : color,
                      border: '3px solid white',
                      boxShadow: '0 0 0 1px #e5e7eb',
                      zIndex: 1
                    }} />

                    {/* Change card */}
                    <div style={{
                      background: change.suspicious_change ? '#fef2f2' : 'white',
                      border: change.suspicious_change ? '2px solid #fca5a5' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '16px',
                      cursor: change.suspicion_reason ? 'pointer' : 'default'
                    }}
                    onClick={() => change.suspicion_reason && toggleChangeExpand(change.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          background: `${color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <Icon size={16} color={color} />
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 600, color: '#111827' }}>
                              {FIELD_LABELS[change.field_name] || change.field_name}
                            </span>
                            {change.suspicious_change && (
                              <span style={{
                                background: '#fee2e2',
                                color: '#dc2626',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}>
                                SUSPICIOUS
                              </span>
                            )}
                            {change.is_significant && !change.suspicious_change && (
                              <span style={{
                                background: '#fef3c7',
                                color: '#d97706',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}>
                                SIGNIFICANT
                              </span>
                            )}
                          </div>

                          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '8px' }}>
                            {formatDate(change.change_detected_at)}
                          </div>

                          {change.field_name !== '_summary' && (
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto 1fr',
                              gap: '12px',
                              alignItems: 'center'
                            }}>
                              <div style={{
                                background: '#f9fafb',
                                padding: '8px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                color: '#6b7280',
                                textAlign: 'right'
                              }}>
                                {change.old_value || '(empty)'}
                              </div>
                              <div style={{ color: '#9ca3af', fontWeight: 600 }}>→</div>
                              <div style={{
                                background: change.suspicious_change ? '#fee2e2' : '#ecfdf5',
                                padding: '8px',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                color: change.suspicious_change ? '#dc2626' : '#059669',
                                fontWeight: 500
                              }}>
                                {change.new_value || '(empty)'}
                              </div>
                            </div>
                          )}

                          {isExpanded && change.suspicion_reason && (
                            <div style={{
                              marginTop: '12px',
                              padding: '12px',
                              background: '#fef2f2',
                              borderRadius: '6px',
                              borderLeft: '3px solid #ef4444'
                            }}>
                              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626', marginBottom: '4px' }}>
                                WHY THIS IS SUSPICIOUS:
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {change.suspicion_reason}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          background: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {summary.firstChangeDate && summary.lastChangeDate && (
              <>
                Tracking since {new Date(summary.firstChangeDate).toLocaleDateString()} •
                Last change {new Date(summary.lastChangeDate).toLocaleDateString()}
              </>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
